import { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { BarChart3, Bell, Check, ChevronDown, DollarSign, Layers, LogOut, Menu, ShieldCheck, Truck, User, X } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useUserProfile } from '../../context/UserProfileContext';
import { useNotifications } from '../../hooks/useNotifications';
import { NotificationPanel } from '../notifications/NotificationPanel';

export function TopBar() {
  const { signOut } = useAuth();
  const navigate = useNavigate();
  const { currentUser, adminOrg, memberships, activeOrg, setActiveOrg } = useUserProfile();
  const orgName = activeOrg?.orgName;
  const hasAnyAdminAccess = currentUser?.isPlatformAdmin || !!adminOrg;
  const dispatchEnabled = activeOrg?.featureDispatch !== false;
  const fleetEnabled = activeOrg?.featureFleetManagement ?? false;
  // My Moves and Dashboard are only relevant when the org has dispatch, jobs, or lead pipeline
  const hasMovesFeatures = dispatchEnabled
    || activeOrg?.featureJobs !== false
    || (activeOrg?.featureLeadPipeline ?? false);
  // Operations page is visible if either dispatch or fleet is enabled
  const operationsEnabled = dispatchEnabled || fleetEnabled;
  const hasMultipleOrgs = memberships.length > 1;
  const [open, setOpen] = useState(false);
  const [orgMenuOpen, setOrgMenuOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [avatarMenuOpen, setAvatarMenuOpen] = useState(false);
  const ref = useRef<HTMLElement>(null);
  const {
    notifications, loading: notifLoading, error: notifError, unreadCount,
    hasMore: notifHasMore, fetchMore: notifFetchMore,
    fetchNotifications: notifFetch,
    markRead: notifMarkRead, markAllRead: notifMarkAllRead,
  } = useNotifications(activeOrg?.orgId ?? null);

  useEffect(() => {
    if (!open && !orgMenuOpen && !avatarMenuOpen) return;
    function onPointerDown(e: PointerEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
        setOrgMenuOpen(false);
        setAvatarMenuOpen(false);
      }
    }
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open, orgMenuOpen, avatarMenuOpen]);

  function handleSwitchOrg(orgId: string) {
    setActiveOrg(orgId);
    setOrgMenuOpen(false);
    setOpen(false);
    navigate('/');
  }

  // User initials for avatar fallback
  const initials = (currentUser?.displayName || currentUser?.email || '?')
    .split(/\s+/)
    .map(w => w[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

  return (
    <header
      ref={ref}
      className="fixed top-0 left-0 right-0 z-[200] bg-plum"
      style={{ paddingTop: 'env(safe-area-inset-top)' }}
    >
      {/* Title row */}
      <div
        className="flex items-center justify-between px-4 md:px-6"
        style={{ height: 'var(--header-height)' }}
      >
        <div className="flex items-center gap-0 min-w-0 max-w-[60vw] md:max-w-none">
          <Link
            to="/"
            data-testid="app-brand"
            className="flex items-center gap-2 font-display font-bold text-white tracking-tight leading-tight
                       hover:text-white/80 transition-colors duration-150 truncate"
          >
            <img src="/cubed_out_white.svg" alt="iomi pro" className="h-8 w-8" />
            {orgName && (
              <span className="text-base font-normal opacity-75"> × {orgName}</span>
            )}
          </Link>

          {/* Org switcher — visible when user belongs to multiple orgs */}
          {hasMultipleOrgs && (
            <div className="relative">
              <button
                type="button"
                onClick={() => setOrgMenuOpen(o => !o)}
                aria-label="Switch organization"
                className="flex items-center justify-center w-9 h-9 rounded-lg ml-0.5
                           text-white/60 hover:text-white hover:bg-white/10 active:bg-white/20
                           transition-colors duration-150"
              >
                <ChevronDown size={16} className={`transition-transform ${orgMenuOpen ? 'rotate-180' : ''}`} />
              </button>

              {orgMenuOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setOrgMenuOpen(false)} />
                  <ul className="absolute left-0 top-full mt-1.5 z-50 w-56 border border-stone
                                 rounded-xl shadow-brand-md overflow-hidden"
                      style={{ backgroundColor: 'var(--color-surface)' }}>
                    {memberships.map(m => (
                      <li key={m.orgId}>
                        <button
                          type="button"
                          onClick={() => handleSwitchOrg(m.orgId)}
                          className={`w-full text-left px-3 py-2.5 text-[13px] flex items-center gap-2
                                     hover:bg-mist transition-colors border-b border-stone last:border-0
                                     ${m.orgId === activeOrg?.orgId
                                       ? 'font-semibold text-plum bg-plum/5'
                                       : 'text-primary-theme'}`}
                        >
                          <span className="flex-1 truncate">{m.orgName}</span>
                          {m.orgId === activeOrg?.orgId && (
                            <Check size={14} className="shrink-0 text-plum" />
                          )}
                        </button>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </div>
          )}
        </div>

        {/* Inline nav — md and up */}
        <nav className="hidden md:flex items-center gap-1" aria-label="Main navigation">
          {hasMovesFeatures && (
            <NavLink
              to="/"
              end
              className={({ isActive }) =>
                `flex items-center gap-2 px-3 h-9 rounded-lg text-[13px] font-medium transition-colors duration-150
                 ${isActive ? 'bg-white/20 text-white' : 'text-white/70 hover:text-white hover:bg-white/10'}`
              }
            >
              <Truck size={15} />
              My Moves
            </NavLink>
          )}
          <NavLink
            to="/sales"
            className={({ isActive }) =>
              `flex items-center gap-2 px-3 h-9 rounded-lg text-[13px] font-medium transition-colors duration-150
               ${isActive ? 'bg-white/20 text-white' : 'text-white/70 hover:text-white hover:bg-white/10'}`
            }
          >
            <DollarSign size={15} />
            Sales
          </NavLink>

          {hasAnyAdminAccess && operationsEnabled && (
            <NavLink
              to="/operations"
              className={({ isActive }) =>
                `flex items-center gap-2 px-3 h-9 rounded-lg text-[13px] font-medium transition-colors duration-150
                 ${isActive ? 'bg-white/20 text-white' : 'text-white/70 hover:text-white hover:bg-white/10'}`
              }
            >
              <Layers size={15} />
              Operations
            </NavLink>
          )}

          {hasAnyAdminAccess && hasMovesFeatures && (
            <NavLink
              to="/dashboard"
              end
              className={({ isActive }) =>
                `flex items-center gap-2 px-3 h-9 rounded-lg text-[13px] font-medium transition-colors duration-150
                 ${isActive ? 'bg-white/20 text-white' : 'text-white/70 hover:text-white hover:bg-white/10'}`
              }
            >
              <BarChart3 size={15} />
              Dashboard
            </NavLink>
          )}

          {/* Divider */}
          <div className="ml-1 w-px h-5 bg-white/20" />

          {/* Notification bell — desktop */}
          <div className="relative">
            <button
              type="button"
              onClick={() => { setNotifOpen(o => { if (!o) notifFetch(); return !o; }); setAvatarMenuOpen(false); }}
              aria-label="Notifications"
              className="flex items-center justify-center w-9 h-9 rounded-lg text-white/70
                         hover:text-white hover:bg-white/10 active:bg-white/20
                         transition-colors duration-150"
            >
              <Bell size={18} />
              {unreadCount > 0 && (
                <span className="absolute top-0.5 right-0.5 min-w-[16px] h-[16px] flex items-center justify-center
                                 rounded-full bg-rose text-white text-[9px] font-bold px-0.5 leading-none">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </button>

            {notifOpen && (
              <NotificationPanel
                notifications={notifications}
                loading={notifLoading}
                error={notifError}
                hasMore={notifHasMore}
                onClose={() => setNotifOpen(false)}
                onMarkRead={notifMarkRead}
                onMarkAllRead={notifMarkAllRead}
                onLoadMore={notifFetchMore}
              />
            )}
          </div>

          {/* User avatar with dropdown — desktop */}
          <div className="relative">
            <button
              type="button"
              onClick={() => { setAvatarMenuOpen(o => !o); setNotifOpen(false); }}
              aria-label="User menu"
              className="flex items-center justify-center w-9 h-9 rounded-full overflow-hidden
                         ring-2 ring-white/30 hover:ring-white/60 transition-all duration-150"
            >
              {currentUser?.photoBase64 ? (
                <img
                  src={currentUser.photoBase64}
                  alt={currentUser.displayName || 'Profile'}
                  className="w-full h-full object-cover"
                />
              ) : (
                <span className="w-full h-full flex items-center justify-center bg-white/20 text-white text-[11px] font-bold">
                  {initials}
                </span>
              )}
            </button>

            {avatarMenuOpen && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setAvatarMenuOpen(false)} />
                <div className="absolute right-0 top-full mt-1.5 z-50 w-48 border border-stone
                                rounded-xl shadow-brand-md overflow-hidden"
                     style={{ backgroundColor: 'var(--color-surface)' }}>
                  <NavLink
                    to="/profile"
                    onClick={() => setAvatarMenuOpen(false)}
                    className={({ isActive }) =>
                      `flex items-center gap-2.5 px-3.5 py-2.5 text-[13px] font-medium transition-colors
                       border-b border-stone
                       ${isActive ? 'text-plum bg-plum/5' : 'text-primary-theme hover:bg-mist'}`
                    }
                  >
                    <User size={15} />
                    My Profile
                  </NavLink>
                  {hasAnyAdminAccess && (
                    <NavLink
                      to="/admin"
                      onClick={() => setAvatarMenuOpen(false)}
                      className={({ isActive }) =>
                        `flex items-center gap-2.5 px-3.5 py-2.5 text-[13px] font-medium transition-colors
                         border-b border-stone
                         ${isActive ? 'text-plum bg-plum/5' : 'text-primary-theme hover:bg-mist'}`
                      }
                    >
                      <ShieldCheck size={15} />
                      Admin Panel
                    </NavLink>
                  )}
                  <button
                    type="button"
                    onClick={() => { setAvatarMenuOpen(false); signOut(); }}
                    className="flex w-full items-center gap-2.5 px-3.5 py-2.5 text-[13px] font-medium
                               text-slate hover:bg-mist hover:text-primary-theme transition-colors"
                  >
                    <LogOut size={15} />
                    Sign out
                  </button>
                </div>
              </>
            )}
          </div>
        </nav>

        <div className="flex items-center gap-1">
          {/* Notification bell — mobile only */}
          <div className="relative md:hidden">
            <button
              type="button"
              onClick={() => { setNotifOpen(o => { if (!o) notifFetch(); return !o; }); setOpen(false); setOrgMenuOpen(false); }}
              aria-label="Notifications"
              className="flex items-center justify-center w-11 h-11 rounded-lg text-white/80
                         hover:text-white hover:bg-white/10 active:bg-white/20
                         transition-colors duration-150"
            >
              <Bell size={20} />
              {unreadCount > 0 && (
                <span className="absolute top-1.5 right-1.5 min-w-[18px] h-[18px] flex items-center justify-center
                                 rounded-full bg-rose text-white text-[10px] font-bold px-1 leading-none">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </button>

            {notifOpen && (
              <NotificationPanel
                notifications={notifications}
                loading={notifLoading}
                error={notifError}
                hasMore={notifHasMore}
                onClose={() => setNotifOpen(false)}
                onMarkRead={notifMarkRead}
                onMarkAllRead={notifMarkAllRead}
                onLoadMore={notifFetchMore}
              />
            )}
          </div>

          {/* Hamburger — mobile only */}
          <button
            type="button"
            onClick={() => setOpen(o => !o)}
            aria-label={open ? 'Close menu' : 'Open menu'}
            aria-expanded={open}
            className="md:hidden flex items-center justify-center w-11 h-11 rounded-lg text-white/80
                       hover:text-white hover:bg-white/10 active:bg-white/20
                       transition-colors duration-150"
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {/* Dropdown — mobile only */}
      {open && (
        <nav
          className="md:hidden border-t border-stone shadow-md"
          style={{ backgroundColor: 'var(--color-surface)' }}
          aria-label="Main menu"
        >
          {/* Section 1: Org switcher */}
          {hasMultipleOrgs && (
            <div className="border-b border-stone">
              <p className="px-5 pt-3 pb-1 text-[11px] font-semibold text-slate uppercase tracking-wider">
                Organization
              </p>
              {memberships.map(m => (
                <button
                  key={m.orgId}
                  type="button"
                  onClick={() => handleSwitchOrg(m.orgId)}
                  className={`w-full flex items-center gap-3 px-5 py-3 text-sm font-medium transition-colors
                             ${m.orgId === activeOrg?.orgId
                               ? 'text-plum bg-plum/5'
                               : 'text-primary-theme hover:bg-mist'}`}
                >
                  <span className="flex-1 text-left truncate">{m.orgName}</span>
                  {m.orgId === activeOrg?.orgId && (
                    <Check size={16} className="shrink-0 text-plum" />
                  )}
                </button>
              ))}
            </div>
          )}

          {/* Section 2: Primary nav items */}
          <div className="border-b border-stone">
            {hasMovesFeatures && (
              <NavLink
                to="/"
                end
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-5 py-4 text-sm font-medium transition-colors
                   ${isActive ? 'text-plum bg-plum/5' : 'text-primary-theme hover:bg-mist'}`
                }
              >
                <Truck size={18} />
                My Moves
              </NavLink>
            )}
            <NavLink
              to="/sales"
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 px-5 py-4 text-sm font-medium transition-colors
                 ${isActive ? 'text-plum bg-plum/5' : 'text-primary-theme hover:bg-mist'}`
              }
            >
              <DollarSign size={18} />
              Sales
            </NavLink>

            {hasAnyAdminAccess && operationsEnabled && (
              <NavLink
                to="/operations"
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-5 py-4 text-sm font-medium transition-colors
                   ${isActive ? 'text-plum bg-plum/5' : 'text-primary-theme hover:bg-mist'}`
                }
              >
                <Layers size={18} />
                Operations
              </NavLink>
            )}

            {hasAnyAdminAccess && hasMovesFeatures && (
              <NavLink
                to="/dashboard"
                end
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-5 py-4 text-sm font-medium transition-colors
                   ${isActive ? 'text-plum bg-plum/5' : 'text-primary-theme hover:bg-mist'}`
                }
              >
                <BarChart3 size={18} />
                Dashboard
              </NavLink>
            )}
          </div>

          {/* Section 3: Profile & Admin */}
          <div className="border-b border-stone">
            <NavLink
              to="/profile"
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 px-5 py-4 text-sm font-medium transition-colors
                 ${isActive ? 'text-plum bg-plum/5' : 'text-primary-theme hover:bg-mist'}`
              }
            >
              <User size={18} />
              My Profile
            </NavLink>

            {hasAnyAdminAccess && (
              <NavLink
                to="/admin"
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-5 py-4 text-sm font-medium transition-colors
                   ${isActive ? 'text-plum bg-plum/5' : 'text-primary-theme hover:bg-mist'}`
                }
              >
                <ShieldCheck size={18} />
                Admin Panel
              </NavLink>
            )}
          </div>

          {/* Section 4: Sign out */}
          <div>
            <button
              type="button"
              onClick={() => { setOpen(false); signOut(); }}
              className="flex w-full items-center gap-3 px-5 py-4 text-sm font-medium
                         text-slate hover:bg-mist hover:text-primary-theme transition-colors"
            >
              <LogOut size={18} />
              Sign out
            </button>
          </div>
        </nav>
      )}
    </header>
  );
}
